import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';
import { useWindowSize } from 'usehooks-ts';

import { Button } from 'js/design_system/Button';
import { Menu } from 'js/design_system/Menu';
import { MenuItem } from 'js/design_system/MenuItem';
import { TextButton } from 'js/design_system/TextButton';
import CitationModal from 'js/journals/CitationModal';
import trackClick from 'js/utils/trackClick';
import { useCitations } from './hooks/useCitations';
import _SSRWrapper from "LoswpMoreActionsMenu-SSRWrapper.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/single_work/loswp/LoswpMoreActionsMenu.tsx?styleId=SSRWrapper";


const SSRWrapper = /*#__PURE__*/styled("div", null, {
  displayName: "SSRWrapper",
  styles: _SSRWrapper
});

type Props = {
  workId: number;
  loswpDesign: string;
  workUrl: string;
};

export const LoswpMoreActionsMenu: React.FC<Props> = ({
  workId,
  loswpDesign,
  workUrl,
}) => {
  const [citationModalOpen, setCitationModalOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const { width } = useWindowSize();
  // Use TextButton for screens smaller than 796px (this is the breakpoint for which ctas don't
  // fit on the screen anymore), Button for larger screens
  const isTextButton = width < 796;
  const { citations, loadCitations } = useCitations(workId);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted) {
      loadCitations();
    }
  }, [workId, loadCitations, isMounted]);

  const openCitationsModal = () => {
    setCitationModalOpen(true);
  };

  const closeCitationsModal = () => {
    setCitationModalOpen(false);
  };

  const handleSave = async () => {
    trackClick({
      source: 'loswp--bookmark-button--work-card',
      bucket: loswpDesign,
      entityId: workId,
      extra: { source_work_id: workId },
    });

    const { openLoginModal } = await import('js/login_modal/LoginModalManager');
    openLoginModal({
      postLoginRedirectUrl: workUrl,
      clickTrackingContext: {
        source: 'loswp--bookmark-button--work-card',
        bucket: loswpDesign,
        entityId: workId,
        extra: { source_work_id: workId },
      },
      registrationParams: {
        user_source: 'download',
      },
    });
  };

  const handleShare = async () => {
    trackClick({
      source: 'loswp--share-button--work-card',
      bucket: loswpDesign,
      entityId: workId,
      extra: { source_work_id: workId },
    });

    const { openLoginModal } = await import('js/login_modal/LoginModalManager');
    openLoginModal({
      postLoginRedirectUrl: workUrl,
      clickTrackingContext: {
        source: 'loswp--share-button--work-card',
        bucket: loswpDesign,
        entityId: workId,
        extra: { source_work_id: workId },
      },
      registrationParams: {
        user_source: 'download',
      },
    });
  };

  const handleTranslate = async () => {
    trackClick({
      source: 'loswp--translate-button--work-card',
      bucket: loswpDesign,
      entityId: workId,
      extra: { source_work_id: workId },
    });

    const { openLoginModal } = await import('js/login_modal/LoginModalManager');

    const url = workUrl.startsWith('http')
      ? new URL(workUrl)
      : new URL(workUrl, window.location.origin);
    url.searchParams.set('show_translation', 'true');
    const redirectUrl = url.toString();

    openLoginModal({
      postLoginRedirectUrl: redirectUrl,
      clickTrackingContext: {
        source: 'loswp--translate-button--work-card',
        bucket: loswpDesign,
        entityId: workId,
        extra: { source_work_id: workId },
      },
      registrationParams: {
        user_source: 'translation',
        redirect_url: redirectUrl,
      },
    });
  };

  const button = isTextButton ? (
    <TextButton icon="more_vert" inline>
      More actions
    </TextButton>
  ) : (
    <Button icon="more_vert" secondary />
  );

  if (!isMounted) {
    return <SSRWrapper>{button}</SSRWrapper>;
  }

  return (
    <>
      <Menu
        placement={isTextButton ? 'top-end' : 'bottom-end'}
        offset={10}
        render={() => (
          <>
            <MenuItem icon="bookmark" onClick={handleSave}>
              Save
            </MenuItem>
            <MenuItem icon="format_quote" onClick={openCitationsModal}>
              Cite
            </MenuItem>
            <MenuItem icon="translate" onClick={handleTranslate}>
              Translate
            </MenuItem>
            <MenuItem icon="share" onClick={handleShare}>
              Share
            </MenuItem>
          </>
        )}
      >
        {button}
      </Menu>
      <CitationModal
        open={citationModalOpen}
        citations={citations}
        onClose={closeCitationsModal}
        source="swp_citation_modal"
        entity_id={String(workId)}
      />
    </>
  );
};

export default LoswpMoreActionsMenu;
